/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiThemeProvider, useEuiTheme, useGeneratedHtmlId } from '../../../services';
import { EuiAccordion } from '../../accordion';
import { EuiIcon } from '../../icon';
import { EuiFlexGroup, EuiFlexItem } from '../../flex';
import { EuiTitle } from '../../title';
import { euiCollapsibleNavGroupStyles } from './collapsible_nav_group.styles';
export var BACKGROUNDS = ['none', 'light', 'dark'];
export var EuiCollapsibleNavGroup = ({
  className,
  children,
  id,
  title,
  iconType,
  iconSize = 'l',
  background = 'none',
  isCollapsible = false,
  titleElement = 'h3',
  titleSize = 'xxs',
  iconProps,
  ...rest
}) => {
  const groupID = useGeneratedHtmlId({
    conditionalId: id
  });
  const titleID = `${groupID}__title`;
  const euiTheme = useEuiTheme();
  const styles = euiCollapsibleNavGroupStyles(euiTheme);
  const cssStyles = [styles.euiCollapsibleNavGroup, isCollapsible ? styles.isCollapsible : styles.notCollapsible, background && styles[background]];
  const classes = classNames('euiCollapsibleNavGroup', className);

  // Warn if consumer passes an iconType without a title
  if (iconType && !title) {
    console.warn('EuiCollapsibleNavGroup will not render an icon without `title`.');
  }
  const childrenStyles = [styles.childrenWrapper.euiCollapsibleNavGroup__children, title && styles.childrenWrapper.withHeading];
  const content = children && <div css={childrenStyles} className="euiCollapsibleNavGroup__children">
      {children}
    </div>;
  const headingClasses = 'euiCollapsibleNavGroup__heading';
  const TitleElement = titleElement;
  const titleContent = title ? <EuiFlexGroup gutterSize="m" alignItems="center" responsive={false}>
      {iconType && <EuiFlexItem grow={false}>
          <EuiIcon {...iconProps} type={iconType} size={iconSize} />
        </EuiFlexItem>}

      <EuiFlexItem>
        <EuiTitle size={titleSize}>
          <TitleElement id={titleID} className="euiCollapsibleNavGroup__title">
            {title}
          </TitleElement>
        </EuiTitle>
      </EuiFlexItem>
    </EuiFlexGroup> : undefined;
  let render;
  if (isCollapsible && title) {
    render = <EuiAccordion id={groupID} css={cssStyles} className={classes} buttonClassName={headingClasses} buttonContent={titleContent} initialIsOpen={true} arrowDisplay="right" {...rest}>
        {content}
      </EuiAccordion>;
  } else {
    render = <div id={groupID} css={cssStyles} className={classes} {...rest}>
        {titleContent && <div className={headingClasses}>{titleContent}</div>}
        {content}
      </div>;
  }
  return background === 'dark' ? <EuiThemeProvider colorMode="dark" wrapperProps={{
    cloneElement: true
  }}>
      {render}
    </EuiThemeProvider> : render;
};
EuiCollapsibleNavGroup.propTypes = {
  /**
     * ReactNode to render as this component's content
     */
  /**
     * ReactNode to render as this component's content
     */
  children: PropTypes.node,
  /**
     * Sits left of the `title` and only when `title` is present
     */
  iconType: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "article", "asterisk", "at", "auditbeatApp", "beaker", "bell", "bellSlash", "beta", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "cluster", "codeApp", "color", "compute", "console", "consoleApp", "container", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "discuss", "document", "documentEdit", "documentation", "documents", "dot", "dotInCircle", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "endpoint", "eql", "eraser", "error", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "filterExclude", "filterIgnore", "filterInclude", "filterInCircle", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "grabOmnidirectional", "gradient", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "indexTemporary", "infinity", "inputOutput", "inspect", "invert", "ip", "key", "keyboard", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "kubernetesNode", "kubernetesPod", "launch", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoVulnerabilityManagement", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "namespace", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "palette", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "pivot", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortAscending", "sortDescending", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spaces", "spacesApp", "sparkles", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelineWithArrow", "timelionApp", "timeRefresh", "timeslider", "training", "transitionLeftIn", "transitionLeftOut", "transitionTopIn", "transitionTopOut", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "vulnerabilityManagementApp", "warning", "alert", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenAlias", "tokenAnnotation", "tokenArray", "tokenBinary", "tokenBoolean", "tokenClass", "tokenCompletionSuggester", "tokenConstant", "tokenDate", "tokenElement", "tokenEnum", "tokenEnumMember", "tokenEvent", "tokenException", "tokenField", "tokenFile", "tokenFlattened", "tokenFunction", "tokenGeo", "tokenHistogram", "tokenInterface", "tokenIP", "tokenJoin", "tokenKey", "tokenKeyword", "tokenMethod", "tokenMetricCounter", "tokenMetricGauge", "tokenModule", "tokenNamespace", "tokenNested", "tokenNull", "tokenNumber", "tokenObject", "tokenOperator", "tokenPackage", "tokenParameter", "tokenPercolator", "tokenProperty", "tokenRange", "tokenRankFeature", "tokenRankFeatures", "tokenRepo", "tokenSearchType", "tokenShape", "tokenString", "tokenStruct", "tokenSymbol", "tokenTag", "tokenText", "tokenTokenCount", "tokenVariable", "tokenVectorDense", "tokenDenseVector", "tokenVectorSparse"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]),
  /**
     * Change the size of the icon in the `title`
     */
  iconSize: PropTypes.any,
  /**
     * Further extend the props applied to EuiIcon
     */
  iconProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any,
    /**
         * Descriptive title for naming the icon based on its use
         */
    title: PropTypes.string,
    /**
         * A unique identifier for the title element
         */
    titleId: PropTypes.string,
    /**
         * Its value should be one or more element IDs
         */
    "aria-labelledby": PropTypes.string,
    /**
         * Callback when the icon has been loaded & rendered
         */
    onIconLoad: PropTypes.func
  }),
  /**
     * Optionally provide an id, otherwise one will be created
     */
  /**
     * Optionally provide an id, otherwise one will be created
     */
  id: PropTypes.string,
  /**
     * Adds a background color to the entire group,
     * applying the correct text color to the `title` only
     */
  /**
     * Adds a background color to the entire group,
     * applying the correct text color to the `title` only
     */
  background: PropTypes.any,
  /**
     * Determines the title's heading element
     */
  /**
     * Determines the title's heading element
     */
  titleElement: PropTypes.oneOf(["h2", "h3", "h4", "h5", "h6", "span"]),
  /**
     * Title sizing equivalent to EuiTitle, but only `s` and smaller
     */
  /**
     * Title sizing equivalent to EuiTitle, but only `s` and smaller
     */
  titleSize: PropTypes.any,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  /**
       * Applied to the entire .euiAccordion wrapper.
       * When using `fieldset`, it will enforce `buttonElement = legend` as well.
       */
  element: PropTypes.oneOf(["div", "fieldset"]),
  /**
       * Defaults to the [group role](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/group_role).
       *
       * If your accordion contains significant enough content to be a document
       * [landmark role](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/region_role#accessibility_concerns), consider using the `region` role instead.
       * @default group
       */
  role: PropTypes.any,
  /**
       * Class that will apply to the trigger for the accordion.
       */
  buttonClassName: PropTypes.string,
  /**
       * Apply more props to the triggering button.
       *
       * Includes optional `paddingSize` prop which allows sizes of `s`, `m`, or `l`.
       * Note: Padding will not be present on the side closest to the accordion arrow.
       */
  buttonProps: PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    css: PropTypes.any,
    paddingSize: PropTypes.oneOf(["s", "m", "l"])
  }),
  /**
       * Class that will apply to the trigger content for the accordion.
       */
  buttonContentClassName: PropTypes.string,
  /**
       * The content of the clickable trigger
       */
  buttonContent: PropTypes.node,
  /**
       * Applied to the main button receiving the `onToggle` event.
       * Anything other than the default `button` does not support removing the arrow display (for accessibility of focus).
       */
  buttonElement: PropTypes.oneOf(["div", "legend", "button"]),
  /**
       * Extra props to pass to the EuiButtonIcon containing the arrow.
       */
  arrowProps: PropTypes.any,
  /**
       * Will appear right aligned against the button. Useful for separate actions like deletions.
       */
  extraAction: PropTypes.node,
  /**
       * The accordion will start in the open state.
       */
  initialIsOpen: PropTypes.bool,
  /**
       * Optional callback method called on open and close with a single `isOpen` parameter
       */
  onToggle: PropTypes.func,
  /**
       * The padding around the exposed accordion content.
       */
  paddingSize: PropTypes.any,
  /**
       * Placement of the arrow indicator, or 'none' to hide it.
       */
  arrowDisplay: PropTypes.oneOf(["left", "right", "none"]),
  /**
       * Optional border styling. Defaults to 'none'.
       */
  borders: PropTypes.oneOf(["horizontal", "all", "none"]),
  /**
       * Control the opening of accordion via prop
       */
  forceState: PropTypes.oneOf(["closed", "open"]),
  /**
       * Change `extraAction` and children into a loading spinner
       */
  isLoading: PropTypes.bool,
  /**
       * Choose whether the loading message replaces the content. Customize the message by passing a node
       */
  isLoadingMessage: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.node.isRequired]),
  /**
       * Disable the open/close interaction and visually subdues the trigger
       */
  isDisabled: PropTypes.bool,
  /**
     * If `true`, wraps children in the body of an accordion,
     * requiring the prop `title` to be used as the button.
     * When `false`, simply renders a div without any accordion functionality.
     */
  /**
       * If `true`, wraps children in the body of an accordion,
       * requiring the prop `title` to be used as the button.
       * When `false`, simply renders a div without any accordion functionality.
       */
  isCollapsible: PropTypes.oneOfType([PropTypes.oneOf([true]).isRequired, PropTypes.oneOf([false])]),
  /**
     * The title gets wrapped in the appropriate heading level
     * with the option to add an iconType
     */
  /**
       * The title gets wrapped in the appropriate heading level
       * with the option to add an iconType
       */
  title: PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.node])
};
try {
  EuiCollapsibleNavGroup.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
    description: '',
    displayName: 'EuiCollapsibleNavGroup',
    methods: [],
    props: {
      element: {
        defaultValue: null,
        description: 'Applied to the entire .euiAccordion wrapper.\n' + 'When using `fieldset`, it will enforce `buttonElement = legend` as well.',
        name: 'element',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"div" | "fieldset"',
          value: [{
            value: '"div"'
          }, {
            value: '"fieldset"'
          }]
        }
      },
      isLoading: {
        defaultValue: null,
        description: 'Change `extraAction` and children into a loading spinner',
        name: 'isLoading',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isDisabled: {
        defaultValue: null,
        description: 'Disable the open/close interaction and visually subdues the trigger',
        name: 'isDisabled',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      buttonClassName: {
        defaultValue: null,
        description: 'Class that will apply to the trigger for the accordion.',
        name: 'buttonClassName',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      buttonProps: {
        defaultValue: null,
        description: 'Apply more props to the triggering button.\n' + '\n' + 'Includes optional `paddingSize` prop which allows sizes of `s`, `m`, or `l`.\n' + 'Note: Padding will not be present on the side closest to the accordion arrow.',
        name: 'buttonProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'CommonProps & HTMLAttributes<HTMLElement> & { paddingSize?: "s" | "m" | "l"; }'
        }
      },
      buttonContentClassName: {
        defaultValue: null,
        description: 'Class that will apply to the trigger content for the accordion.',
        name: 'buttonContentClassName',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      buttonContent: {
        defaultValue: null,
        description: 'The content of the clickable trigger',
        name: 'buttonContent',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      buttonElement: {
        defaultValue: null,
        description: 'Applied to the main button receiving the `onToggle` event.\n' + 'Anything other than the default `button` does not support removing the arrow display (for accessibility of focus).',
        name: 'buttonElement',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"button" | "div" | "legend"',
          value: [{
            value: '"button"'
          }, {
            value: '"div"'
          }, {
            value: '"legend"'
          }]
        }
      },
      arrowProps: {
        defaultValue: null,
        description: 'Extra props to pass to the EuiButtonIcon containing the arrow.',
        name: 'arrowProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Partial<Omit<EuiButtonIconProps, "aria-labelledby" | "onClick" | "iconType">>'
        }
      },
      extraAction: {
        defaultValue: null,
        description: 'Will appear right aligned against the button. Useful for separate actions like deletions.',
        name: 'extraAction',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      initialIsOpen: {
        defaultValue: null,
        description: 'The accordion will start in the open state.',
        name: 'initialIsOpen',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      onToggle: {
        defaultValue: null,
        description: 'Optional callback method called on open and close with a single `isOpen` parameter',
        name: 'onToggle',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(isOpen: boolean) => void'
        }
      },
      paddingSize: {
        defaultValue: null,
        description: 'The padding around the exposed accordion content.',
        name: 'paddingSize',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "none" | "m" | "xs" | "l" | "xl"',
          value: [{
            value: '"s"'
          }, {
            value: '"none"'
          }, {
            value: '"m"'
          }, {
            value: '"xs"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }]
        }
      },
      arrowDisplay: {
        defaultValue: null,
        description: "Placement of the arrow indicator, or 'none' to hide it.",
        name: 'arrowDisplay',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"left" | "right" | "none"',
          value: [{
            value: '"left"'
          }, {
            value: '"right"'
          }, {
            value: '"none"'
          }]
        }
      },
      borders: {
        defaultValue: null,
        description: "Optional border styling. Defaults to 'none'.",
        name: 'borders',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"none" | "horizontal" | "all"',
          value: [{
            value: '"none"'
          }, {
            value: '"horizontal"'
          }, {
            value: '"all"'
          }]
        }
      },
      forceState: {
        defaultValue: null,
        description: 'Control the opening of accordion via prop',
        name: 'forceState',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"closed" | "open"',
          value: [{
            value: '"closed"'
          }, {
            value: '"open"'
          }]
        }
      },
      isLoadingMessage: {
        defaultValue: null,
        description: 'Choose whether the loading message replaces the content. Customize the message by passing a node',
        name: 'isLoadingMessage',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/accordion/accordion.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      children: {
        defaultValue: null,
        description: "ReactNode to render as this component's content",
        name: 'children',
        parent: {
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        },
        declarations: [{
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'DOMAttributes'
        }, {
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'DOMAttributes'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      iconType: {
        defaultValue: null,
        description: 'Sits left of the `title` and only when `title` is present',
        name: 'iconType',
        parent: {
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        },
        declarations: [{
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        }],
        required: false,
        type: {
          name: 'IconType'
        }
      },
      iconSize: {
        defaultValue: {
          value: 'l'
        },
        description: 'Change the size of the icon in the `title`',
        name: 'iconSize',
        parent: {
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        },
        declarations: [{
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "m" | "l" | "xl" | "original" | "xxl"',
          value: [{
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }, {
            value: '"original"'
          }, {
            value: '"xxl"'
          }]
        }
      },
      iconProps: {
        defaultValue: null,
        description: 'Further extend the props applied to EuiIcon',
        name: 'iconProps',
        parent: {
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        },
        declarations: [{
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        }],
        required: false,
        type: {
          name: 'Omit<EuiIconProps, "size" | "type">'
        }
      },
      id: {
        defaultValue: null,
        description: 'Optionally provide an id, otherwise one will be created',
        name: 'id',
        parent: {
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        },
        declarations: [{
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      background: {
        defaultValue: {
          value: 'none'
        },
        description: 'Adds a background color to the entire group,\n' + 'applying the correct text color to the `title` only',
        name: 'background',
        parent: {
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        },
        declarations: [{
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"light" | "dark" | "none"',
          value: [{
            value: '"light"'
          }, {
            value: '"dark"'
          }, {
            value: '"none"'
          }]
        }
      },
      titleElement: {
        defaultValue: {
          value: 'h3'
        },
        description: "Determines the title's heading element",
        name: 'titleElement',
        parent: {
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        },
        declarations: [{
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"h2" | "h3" | "h4" | "h5" | "h6" | "span"',
          value: [{
            value: '"h2"'
          }, {
            value: '"h3"'
          }, {
            value: '"h4"'
          }, {
            value: '"h5"'
          }, {
            value: '"h6"'
          }, {
            value: '"span"'
          }]
        }
      },
      titleSize: {
        defaultValue: {
          value: 'xxs'
        },
        description: 'Title sizing equivalent to EuiTitle, but only `s` and smaller',
        name: 'titleSize',
        parent: {
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        },
        declarations: [{
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'EuiCollapsibleNavGroupInterface'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "xs" | "xxxs" | "xxs"',
          value: [{
            value: '"s"'
          }, {
            value: '"xs"'
          }, {
            value: '"xxxs"'
          }, {
            value: '"xxs"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/ts5.0/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      isCollapsible: {
        defaultValue: {
          value: 'false'
        },
        description: 'If `true`, wraps children in the body of an accordion,\n' + 'requiring the prop `title` to be used as the button.\n' + 'When `false`, simply renders a div without any accordion functionality.',
        name: 'isCollapsible',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      title: {
        defaultValue: null,
        description: 'The title gets wrapped in the appropriate heading level\n' + 'with the option to add an iconType',
        name: 'title',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'TypeLiteral'
        }, {
          fileName: 'app/src/components/collapsible_nav/collapsible_nav_group/collapsible_nav_group.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      }
    },
    extendedInterfaces: ['EuiCollapsibleNavGroupInterface', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}